/**
 * "Transferring Ownership" modal (Figma 9406:4537, 9410:7007, 9410:7344).
 *
 * Lists the workspace's other active members (`GET /workspaces/{id}/members`),
 * lets the owner search by name or email and pick exactly one teammate as
 * the new **Owner** from each row's role dropdown (other rows may be re-roled
 * at the same time), then calls `POST /workspaces/{id}/transfer-ownership`.
 *
 * The same modal is reused by the account-deletion stepper, which supplies a
 * `stepLabel` ("1 / 3 workspaces") and switches `primaryLabel` between
 * "Next" and "Done".
 *
 * "You need to select a new owner" only appears after the primary button is
 * pressed with nobody picked.  With no teammates to choose from the search
 * box is dropped and, when the caller passes `onInvite`, the primary button
 * becomes "Invite" (the Workspace tab sends the owner to User Management).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Modal, Search, SparkIcon, SparkModalPortal, Spinner, sparkToast } from '@spark';
import UserRoleSelect, { OWNER_ROLE_LABEL, TRANSFER_ROLE_OPTIONS } from '@/features/users/components/UserRoleSelect';
import { listWorkspaceMembers, transferOwnership } from '../../api/workspacesApi';

export const TRANSFER_OWNERSHIP_MODAL_WIDTH = 600;
export const TRANSFER_OWNERSHIP_TITLE = 'Transferring Ownership';
export const TRANSFER_OWNERSHIP_HINT = 'You need to select a new owner';
export const TRANSFER_OWNERSHIP_NO_MEMBERS = 'Invite another teammate to this workspace before transferring ownership.';
export const TRANSFER_OWNERSHIP_NO_MATCHES = 'No teammates match your search.';
export const TRANSFER_OWNERSHIP_LOAD_FAILED = 'Could not load the workspace members.';
export const TRANSFER_OWNERSHIP_SUCCESS = 'Ownership transferred.';

/** Active teammates other than the current owner; invitations are not eligible. */
export function eligibleMembers(members) {
    return (members || []).filter((member) => member?.status === 'active' && !member?.is_owner && member?.email);
}

/** Case-insensitive match on name or email. */
export function matchesQuery(member, query) {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return true;
    return [member.name, member.email].some((field) => String(field || '').toLowerCase().includes(needle));
}

export default function TransferOwnershipModal({
    open,
    workspaceId,
    workspaceName,
    primaryLabel = 'Transfer',
    stepLabel,
    onClose,
    onTransferred,
    onInvite,
}) {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [query, setQuery] = useState('');
    const [roles, setRoles] = useState({});
    const [newOwner, setNewOwner] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // Set when the primary button is pressed without a pick; cleared on a pick or reopen.
    const [showHint, setShowHint] = useState(false);

    useEffect(() => {
        if (!open || !workspaceId) return undefined;
        let cancelled = false;
        setLoading(true);
        setLoadError('');
        setQuery('');
        setNewOwner(null);
        setShowHint(false);
        listWorkspaceMembers(workspaceId).then((result) => {
            if (cancelled) return;
            setLoading(false);
            if (!result.ok) {
                setMembers([]);
                setLoadError(result.error || TRANSFER_OWNERSHIP_LOAD_FAILED);
                return;
            }
            const eligible = eligibleMembers(result.data?.members);
            setMembers(eligible);
            setRoles(Object.fromEntries(eligible.map((member) => [member.email, member.role])));
        });
        return () => {
            cancelled = true;
        };
    }, [open, workspaceId]);

    const visible = useMemo(() => members.filter((member) => matchesQuery(member, query)), [members, query]);
    const originalRole = (email) => members.find((member) => member.email === email)?.role;

    const handleRole = (email, role) => {
        if (role === OWNER_ROLE_LABEL) {
            setRoles((prev) => {
                const next = { ...prev, [email]: OWNER_ROLE_LABEL };
                if (newOwner && newOwner !== email) next[newOwner] = originalRole(newOwner);
                return next;
            });
            setNewOwner(email);
            setShowHint(false);
            return;
        }
        setRoles((prev) => ({ ...prev, [email]: role }));
        if (newOwner === email) setNewOwner(null);
    };

    const close = () => {
        if (submitting) return;
        onClose?.();
    };

    const noMembers = !loading && !loadError && members.length === 0;
    const inviteMode = noMembers && typeof onInvite === 'function';

    const submit = async () => {
        if (submitting) return;
        if (inviteMode) {
            onInvite();
            return;
        }
        if (!newOwner) {
            setShowHint(true);
            return;
        }
        setSubmitting(true);
        const roleChanges = Object.fromEntries(
            Object.entries(roles).filter(
                ([email, role]) => email !== newOwner && role !== OWNER_ROLE_LABEL && role !== originalRole(email),
            ),
        );
        const result = await transferOwnership(workspaceId, { newOwnerEmail: newOwner, roleChanges });
        setSubmitting(false);
        if (!result.ok) {
            sparkToast.error(result.error || 'Could not transfer ownership.');
            return;
        }
        sparkToast.success(TRANSFER_OWNERSHIP_SUCCESS);
        onTransferred?.({ workspace: result.data?.workspace, newOwnerEmail: newOwner });
    };

    let body;
    if (loading) {
        body = (
            <div className="workspace-transfer-modal__status" role="status" aria-live="polite">
                <Spinner size="medium" />
            </div>
        );
    } else if (loadError) {
        body = <p className="workspace-transfer-modal__status workspace-transfer-modal__status--error">{loadError}</p>;
    } else if (members.length === 0) {
        body = <p className="workspace-transfer-modal__empty">{TRANSFER_OWNERSHIP_NO_MEMBERS}</p>;
    } else if (visible.length === 0) {
        body = <p className="workspace-transfer-modal__status">{TRANSFER_OWNERSHIP_NO_MATCHES}</p>;
    } else {
        body = (
            <ul className="workspace-transfer-modal__list" aria-label="Teammates">
                {visible.map((member) => (
                    <li key={member.email} className="workspace-transfer-modal__row">
                        <Avatar
                            size="medium"
                            type={member.picture ? 'image' : 'initials'}
                            src={member.picture || undefined}
                            alt=""
                            primaryText={member.name || member.email}
                            secondaryText={member.email}
                            showContent
                            className="workspace-transfer-modal__member"
                        />
                        <UserRoleSelect
                            role={roles[member.email] || member.role}
                            roles={TRANSFER_ROLE_OPTIONS}
                            onChange={(role) => handleRole(member.email, role)}
                        />
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <SparkModalPortal open={open} onClose={close} width={TRANSFER_OWNERSHIP_MODAL_WIDTH} destroyOnClose>
            <Modal
                open
                className="workspace-danger-modal workspace-transfer-modal"
                title={
                    <span className="workspace-danger-modal__title">
                        <SparkIcon slug="switch-horizontal" size="medium" />
                        {TRANSFER_OWNERSHIP_TITLE}
                        {stepLabel ? <span className="workspace-transfer-modal__step">{stepLabel}</span> : null}
                    </span>
                }
                subtitle={`Select a user you want to make the new owner of ${workspaceName}`}
                showSubtitle
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                showFooterSlot={showHint && !newOwner}
                footerSlot={<span className="workspace-danger-modal__hint">{TRANSFER_OWNERSHIP_HINT}</span>}
                primaryLabel={inviteMode ? 'Invite' : primaryLabel}
                primaryDisabled={submitting || loading || Boolean(loadError) || (noMembers && !inviteMode)}
                tertiaryLabel="Cancel"
                tertiaryDisabled={submitting}
                onPrimaryClick={submit}
                onTertiaryClick={close}
                onCloseClick={close}
            >
                <div className="workspace-transfer-modal__body">
                    {loading || members.length > 0 ? (
                        <Search
                            size="medium"
                            iconPosition="start"
                            placeholder="Search by name or email"
                            value={query}
                            disabled={loading}
                            onChange={(event) => setQuery(event.target.value)}
                            onClear={() => setQuery('')}
                        />
                    ) : null}
                    {body}
                </div>
            </Modal>
        </SparkModalPortal>
    );
}
